import { useState } from "react";
import { Link, useNavigate } from "react-router";
import type { OfferView, OrderRole } from "../../shared/market";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import { imageUrl } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi } from "./api";
import { useMarketCart } from "./cart";
import { StatusPill, useArs } from "./components";
import { useInbox } from "./InboxProvider";

/** People with a listing in favorites for "destacala antes de bajar el precio" on an offer under its price. */
export const FEATURE_NUDGE_FAVORITES = 2;

/** Offers made (buyer) or received (seller), with what each side can do now. */
export function OffersList({ role, featuring }: { role: OrderRole; /** The seller's featured listings left this month (and whether they're still new and can't). */ featuring?: { left: number; newSeller: boolean } }) {
  const { refresh: refreshInbox } = useInbox();
  const { t, locale } = useI18n();
  const ars = useArs();
  const navigate = useNavigate();
  const cart = useMarketCart();
  const loaded = useAsync((signal) => marketApi.offers(signal), []);
  const [offers, setOffers] = useState<OfferView[] | null>(null);
  const [counter, setCounter] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const [featureLeft, setFeatureLeft] = useState(featuring?.left ?? 0);
  const [featured, setFeatured] = useState<Set<number>>(new Set());
  if (loaded.error) return <ErrorMessage onRetry={loaded.reload} />;
  const list = (offers ?? loaded.data)?.filter((o) => o.role === role);
  if (!list) return <Loading />;
  if (!list.length) return <p className="muted">{role === "seller" ? t("mkt.noOffersReceived") : t("mkt.noOffersMade")}</p>;

  const respond = async (id: number, action: "accept" | "reject" | "counter" | "cancel", counterArs?: number) => {
    setBusy(id);
    setFailed(false);
    try {
      setOffers(await marketApi.respondOffer(id, action, counterArs));
      refreshInbox();
    } catch {
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };
  const open = (o: OfferView) => new Date(o.expiresAt) > new Date();
  // An offer under the price on a listing people like, with featuring at hand: feature it before lowering it.
  const nudge = (o: OfferView) =>
    role === "seller" && o.status === "pending" && open(o) && o.priceArs < o.listPriceArs && !o.listingFeatured && (o.listingFavorites ?? 0) >= FEATURE_NUDGE_FAVORITES && featureLeft > 0 && !featuring?.newSeller;
  const feature = async (listingId: number) => {
    setBusy(-listingId);
    setFailed(false);
    try {
      setFeatureLeft((await marketApi.featureListing(listingId)).left);
      setFeatured(new Set([...featured, listingId]));
    } catch {
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {failed && <p className="form-error">{t("error.generic")}</p>}
      <ul className="offer-list">
        {list.map((o) => (
          <li key={o.id} className="offer-row">
            {o.photo && <img src={imageUrl(o.photo)} alt="" width={48} height={48} />}
            <div className="stack-sm">
              <Link to={`/mercado/${o.listingId}`}>
                <strong>{o.listingName}</strong>
              </Link>
              {o.fromReservation && <span className="pill small">{t(o.role === "seller" ? "rsv.pillSeller" : "rsv.pillBuyer")}</span>}
              <span className="small">
                {t("mkt.offerLine", { qty: o.quantity, price: ars(o.priceArs), list: ars(o.listPriceArs) })}
                {o.counterArs && o.status === "countered" ? ` · ${t("mkt.counterLine", { price: ars(o.counterArs) })}` : ""}
              </span>
              {featured.has(o.listingId) ? (
                <span className="small offer-nudge">⭐ {t("offer.featured")}</span>
              ) : (
                nudge(o) && (
                  <span className="small offer-nudge">
                    ⭐ {t("offer.featureFirst", { count: o.listingFavorites ?? 0 })}{" "}
                    <button type="button" className="btn btn-link btn-sm" disabled={busy === -o.listingId} onClick={() => void feature(o.listingId)}>
                      {t("mkt.feature.button", { count: featureLeft })}
                    </button>
                  </span>
                )
              )}
              <span className="muted small">
                {o.other.handle ? `@${o.other.handle}` : o.other.name} · {t("mkt.until", { date: new Date(o.expiresAt).toLocaleString(locale === "es" ? "es-AR" : "en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) })}
              </span>
            </div>
            <StatusPill status={`offer_${o.status}`} />
            <div className="row-actions">
              {role === "seller" && o.status === "pending" && open(o) && (
                <>
                  <button type="button" className="btn btn-primary btn-sm" disabled={busy === o.id} onClick={() => respond(o.id, "accept")}>
                    {t("mkt.accept")}
                  </button>
                  <button type="button" className="btn btn-link btn-sm" disabled={busy === o.id} onClick={() => respond(o.id, "reject")}>
                    {t("mkt.reject")}
                  </button>
                  <span className="counter-input">
                    <input
                      type="number"
                      aria-label={t("mkt.counterAmount")}
                      min={o.priceArs + 1}
                      max={o.listPriceArs - 1}
                      value={counter[o.id] ?? ""}
                      onChange={(e) => setCounter({ ...counter, [o.id]: e.target.value })}
                      placeholder={t("mkt.counterAmount")}
                    />
                    <button type="button" className="btn btn-outline btn-sm" disabled={busy === o.id || !counter[o.id]} onClick={() => respond(o.id, "counter", Number(counter[o.id]))}>
                      {t("mkt.counter")}
                    </button>
                  </span>
                </>
              )}
              {role === "buyer" && o.status === "countered" && open(o) && (
                <>
                  <button type="button" className="btn btn-primary btn-sm" disabled={busy === o.id} onClick={() => respond(o.id, "accept")}>
                    {t("mkt.acceptCounter")}
                  </button>
                  <button type="button" className="btn btn-link btn-sm" disabled={busy === o.id} onClick={() => respond(o.id, "reject")}>
                    {t("mkt.reject")}
                  </button>
                </>
              )}
              {role === "buyer" && o.status === "accepted" && open(o) && o.other.handle && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    cart.add(o.other.handle!, { listingId: o.listingId, quantity: o.quantity, max: o.quantity, name: o.listingName, priceArs: o.priceArs, photo: o.photo, offerId: o.id });
                    navigate(`/mercado/comprar/${o.other.handle}`);
                  }}
                >
                  {t("mkt.buyAtOffer")}
                </button>
              )}
              {role === "buyer" && ["pending", "countered", "accepted"].includes(o.status) && open(o) && (
                <button type="button" className="btn btn-link btn-sm" disabled={busy === o.id} onClick={() => respond(o.id, "cancel")}>
                  {t("mkt.withdraw")}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
